import axios from "axios";
import { getPusher } from "../../../lib/pusher";
import apis from "../../../lib/apis/apis";
import { apiHeaders, apiHeadersImage } from "../../../lib/apiHeader";
import {
  mockGetConversation,
  mockSendGoto,
  mockMarkRead,
  mockSubscribe,
  mockSubscribeInbox,
  mockGetInbox,
} from "./mockGotoData";
import { mockCustomerRows } from "../churn/mockData";

// Flip to false when the Laravel GoTo endpoints (spec §4) ship, then delete
// mockGotoData.js.
export const USE_GOTO_MOCKS = false;

const delay = (data, ms = 400) =>
  new Promise((resolve) => setTimeout(() => resolve(data), ms));

const unwrap = (response) => {
  if (response?.data?.success === "true") return response.data.data;
  throw new Error(response?.data?.message || "Request failed");
};

export const gotoSmsApi = {
  getConversation: async (params) => {
    if (USE_GOTO_MOCKS) return delay(mockGetConversation(params));
    const response = await axios.get(apis.goto_conversation, {
      params,
      headers: apiHeaders(),
    });
    return unwrap(response);
  },

  // Spec §4.3: multipart/form-data end to end — media as real file parts.
  send: async (formData) => {
    if (USE_GOTO_MOCKS) return delay(mockSendGoto(formData), 600);
    const response = await axios.post(apis.goto_send, formData, {
      headers: apiHeadersImage(),
    });
    return unwrap(response);
  },

  // Conversations board list (spec §6.1).
  getInbox: async (params) => {
    if (USE_GOTO_MOCKS) return delay(mockGetInbox(params, mockCustomerRows));
    const response = await axios.get(apis.goto_conversations, {
      params,
      headers: apiHeaders(),
    });
    return unwrap(response);
  },

  markRead: async (customerId) => {
    if (USE_GOTO_MOCKS) return delay(mockMarkRead(customerId));
    const response = await axios.post(
      apis.goto_mark_read,
      { customer_id: customerId },
      { headers: apiHeaders() }
    );
    return unwrap(response);
  },

  // Sidebar badge: { count, provider_id }. provider_id is the resolved clinic
  // id (correct for staff logins) used for the goto-inbox channel.
  unreadCount: async () => {
    if (USE_GOTO_MOCKS) return delay({ count: 0, provider_id: null });
    const response = await axios.get(apis.goto_unread_count, {
      headers: apiHeaders(),
    });
    return unwrap(response);
  },

  // Live inbound messages: mock → reply simulator; real → Pusher channel
  // `goto-sms.{customer_id}`, event `new-message` (spec §4.5). Returns an
  // unsubscribe function.
  subscribe: (customerId, onMessage) => {
    if (USE_GOTO_MOCKS) return mockSubscribe(customerId, onMessage);
    // Shared, persistent connection (see src/lib/pusher.js) — event-listener
    // pattern, no per-subscription connect/disconnect churn.
    const pusher = getPusher();
    const chanName = `goto-sms.${customerId}`;
    const channel = pusher.subscribe(chanName);
    const handler = (msg) => onMessage(msg);
    channel.bind("new-message", handler);
    return () => {
      // Remove only our handler; the per-thread channel is single-consumer so
      // unsubscribing it is safe. Never disconnect the shared socket.
      channel.unbind("new-message", handler);
      pusher.unsubscribe(chanName);
    };
  },

  // Tenant-wide board channel (spec §6.2): real mode listens on
  // `goto-inbox.{clinic_id}`; mock mode uses the simulator's inbox registry.
  // onEvent receives { customer_id, message }.
  subscribeInbox: (clinicId, onEvent) => {
    if (USE_GOTO_MOCKS) return mockSubscribeInbox(onEvent);
    const pusher = getPusher();
    const chanName = `goto-inbox.${clinicId}`;
    const channel = pusher.subscribe(chanName);
    const handler = (evt) => onEvent(evt);
    channel.bind("new-message", handler);
    return () => {
      // The board channel is shared (sidebar badge + Conversations page), so
      // only unbind our handler; leave the channel + shared socket alive.
      channel.unbind("new-message", handler);
    };
  },
};
